import { createSlice } from "@reduxjs/toolkit";
import type { PayloadAction } from "@reduxjs/toolkit";
import { rejectedApiError } from "./rejectedApiError";
// WEB-1909: одно умолчание языка ВОРОНКИ на систему (зеркало серверной
// константы; язык кабинета — другое правило, см. @/i18n).
import { DEFAULT_FUNNEL_LANGUAGE } from "@/utils/defaultFunnelLanguage";
import type { Rule } from "@/logic-engine";
import { pruneDanglingRuleRefs } from "./editor/pruneDanglingRuleRefs";
import type { PaywallCloseMode } from "@/themes/paywallCloseMode";
// WEB-1532: потолок вариантов ответа — зеркало серверной схемы
// (block-schema/questions.ts). Держим и здесь, а не только на кнопке панели:
// addBlockOption зовут не только из панели свойств.
import { MAX_BLOCK_OPTIONS } from "@/constants/blockOptionLimits";
// WEB-2058: заготовки блоков, отказы сохранения, история отмены и генератор
// id вынесены в каталог `./editor/`; реэкспорт держит прежние импорты целыми.
import { generateId } from "./editor/ids";
import { pruneDanglingOptionRefs } from "./editor/optionRefs";
import { newScreen } from "./editor/newScreen";
import { acceptPublishResponse, acceptWriteResponse, isCurrentLoad, isOpenResource, nextOpenSession, whenOpen } from "./editor/openResource";
import { storeHoldsSentDraft } from "./editor/sentDraft";
import { generationIdOf, loadedLogicOf, neverPublishedOf } from "./editor/loadResponse";
import { ownAssetUrlPrefixesOf } from "@/themes/customFonts"; // WEB-3872
import {
  rejectedBuildState,
  rejectedPublishState,
} from "./editor/saveRejection";
export {
  PAYMENT_REQUIRED_SENTINEL,
  TIER_LIMIT_SENTINEL,
  classifySaveRejection,
} from "./editor/saveRejection";
export type { SaveRejection } from "./editor/saveRejection";
import { packBlockSettings, withColorsTouched } from "./editor/packComposition";
import { withLookChoice } from "./editor/originalLook";
import { withCloseMode, withFaviconApplyToFunnel, withFaviconUrl, withLogoUrl, withTheme } from "./editor/appearanceEdits";
import { buildNewBlockDefaults } from "./editor/blockDefaults";
import {
  redoState,
  undoState,
  withHistory,
  withHistoryPayload,
} from "./editor/history";
export { buildNewBlockDefaults, GENERIC_DEFAULT_KINDS } from "./editor/blockDefaults";
// WEB-2552 (пачка 49, заход Р1): разбор содержимого и вторая дверь потолка
// вариантов вынесены в `./editor/parseContent.ts` («перенос токен в токен»).
import {
  parseContent,
  replaceContentState,
  withOptionCapEnforced,
} from "./editor/parseContent";
// WEB-2552 (пачка 49, заход Р1): санки загрузки, смены языка вынесены в
// `./editor/thunks/{load,language}.ts`. Санки сохранения и публикации
// переехали заходом Р2 (ниже) — после того как действие setTierLimitReason
// стало отдельным createAction и перестало держать их в фасаде.
import { loadQuizForEditor, loadPaywallForEditor } from "./editor/thunks/load";
export { loadQuizForEditor, loadPaywallForEditor } from "./editor/thunks/load";
import { changeSourceLanguage } from "./editor/thunks/language";
export { changeSourceLanguage } from "./editor/thunks/language";

// WEB-2552 (пачка 49, заход Р0): типы блоков, фон/раскладка экрана, оформление
// воронки, состояние редактора и три помощника фона переехали в
// `./editor/{types/blocks,types/screen,types/settings,state,screenBackground}.ts`
// («перенос токен в токен» — файл `store/editor/README.md`, будет добавлен
// заходом Р3). Фасад реэкспортирует прежние имена — 682 импортёра, включая 595
// тестов, не переписаны ни на один импорт.
import type {
  BlockType,
  BlockOption,
  BlurTeaserItem,
  FeatureChipItem,
  NowGoalRow,
  BmiZone,
  AccordionItem,
  TestimonialItem,
  ImageChoiceOption,
  LoaderStep,
  LoaderQuestion,
  PlanSelectorPlan,
  PaymentBannerProvider,
  WalletProvider,
  CalloutType,
  DividerStyle,
  EditorBlock,
} from "./editor/types/blocks";
export type {
  BlockType,
  BlockOption,
  BlurTeaserItem,
  FeatureChipItem,
  NowGoalRow,
  BmiZone,
  AccordionItem,
  TestimonialItem,
  ImageChoiceOption,
  LoaderStep,
  LoaderQuestion,
  PlanSelectorPlan,
  PaymentBannerProvider,
  WalletProvider,
  CalloutType,
  DividerStyle,
  EditorBlock,
};

import type {
  ScreenBackground,
  ScreenLayout,
  EditorScreen,
} from "./editor/types/screen";
export type { ScreenBackground, ScreenLayout, EditorScreen };

import type {
  FunnelBackgroundSolid,
  FunnelBackgroundGradient,
  FunnelBackground,
  FunnelPalette,
  EditorSettings,
} from "./editor/types/settings";
export type {
  FunnelBackgroundSolid,
  FunnelBackgroundGradient,
  FunnelBackground,
  FunnelPalette,
  EditorSettings,
};

import type {
  AiChatEntry,
  DownsellOfferPreviewState,
  EditorState,
  ContentSnapshot,
} from "./editor/state";
export type {
  AiChatEntry,
  DownsellOfferPreviewState,
  EditorState,
  ContentSnapshot,
};
import { initialState } from "./editor/state";

import {
  withLayoutKey,
  withBackgroundKey,
  withRenderableImageMode,
} from "./editor/screenBackground";
import { addScreenFieldCases } from "./editor/screenFieldActions";
export { setScreenBackgroundImageTone, setScreenChromeFlags, setQuizHeader, setQuizCta, addCustomFontFile, removeCustomFont, setFunnelFontId, setScreenPaddingX, takePackColors, setBlockOptionNextScreen } from "./editor/screenFieldActions";

// WEB-2552 (пачка 49, заход Р2): санки сохранения (вместе с DraftSaveSnapshot)
// и публикации вынесены в `./editor/thunks/{save,publish}.ts` («перенос токен
// в токен»); их extraReducers остались ниже в этом файле.
import { saveQuizDraft, savePaywallDraft } from "./editor/thunks/save";
export { saveQuizDraft, savePaywallDraft } from "./editor/thunks/save";
export type { DraftSaveSnapshot } from "./editor/thunks/save";
import { publishQuiz, publishPaywall } from "./editor/thunks/publish";
export { publishQuiz, publishPaywall } from "./editor/thunks/publish";
// WEB-2552 Р2 / WEB-2704: действия вне createSlice (`./editor/actions.ts`) —
// причина тарифного отказа (санки выше не тянут фасад) и «Дублировать» (тела —
// `./editor/duplicateState.ts`); срез ловит их в extraReducers, имена прежние.
import { setTierLimitReason, duplicateScreen, duplicateBlock } from "./editor/actions";
export { setTierLimitReason, duplicateScreen, duplicateBlock } from "./editor/actions";
import { duplicateBlockState, duplicateScreenState } from "./editor/duplicateState";

/**
 * WEB-1988: сам резолвер переехал из среза отдельным модулем (WEB-2062 —
 * `player/planSelectorPlans.ts`, общий код плеера) — его зовёт
 * рендерер блоков на странице ПОКУПАТЕЛЯ, и ради четырёх строк в бандл воронки
 * приезжал весь этот срез (а следом стор проектов и организаций). Здесь
 * остаётся реэкспорт: кабинетные потребители (панель свойств, прайс-гейт)
 * импортируют его отсюда как раньше, правило по-прежнему одно на всех.
 */
export { resolvePlanSelectorPlans } from '@/player/planSelectorPlans';

/**
 * WEB-2382 п.6 — СЛУЖЕБНЫЕ ДЕЙСТВИЯ СРЕЗА: не правка автора руками.
 *
 * Матчер WEB-608 ниже помечает «автор правил руками после хода» по любому
 * действию `editor/*` у грязного черновика. Под него попадали и действия,
 * которые делает сам редактор сразу после хода (снимок сверки, запись в ленту
 * чата, настройки хода) и просто выбор экрана мышью — из-за чего
 * предупреждение «потеряете ручные правки» при отмене хода показывалось
 * ВСЕГДА, и там, где правок действительно не было.
 *
 * Перечень — список, а списки молчат про новое, поэтому у правила есть второй
 * конец: сторож `editorSlice.turnApply.web2382.test.ts` вычитывает НАСТОЯЩИЙ
 * набор редьюсеров среза из исходника и требует, чтобы каждый лежал ровно в
 * одной корзине — служебной (этот перечень) или ручной (всё остальное).
 * Новый редьюсер уронит сторож и потребует решения.
 */
export const EDITOR_SERVICE_ACTION_NAMES: readonly string[] = [
  'setTierLimitReason',
  'invalidateDraftSnapshot',
  'setDraftSnapshot',
  'resetEditor',
  'selectScreen',
  'selectBlock',
  'setCookiePreviewVisible',
  'setDownsellPreviewVisible',
  'setDownsellOfferPreviewState',
  'markSaved',
  'markTurnApplied',
  'replaceContent',
  'applyTurnResult',
  'applyTurnSettings',
  'addAiChatEntry',
];

const EDITOR_SERVICE_ACTION_TYPES = new Set(
  EDITOR_SERVICE_ACTION_NAMES.map((name) => `editor/${name}`),
);

const editorSlice = createSlice({
  name: "editor",
  initialState,
  reducers: {
    /**
     * WEB-1333 (п.5): ход ИИ-чата пишет в базу сам, а своего updatedAt не
     * возвращает — снимок сбрасывается, чтобы следующий автосейв не словил
     * ложный 409 от СОБСТВЕННОГО хода. Один сейв идёт без сверки и
     * пересинхронизирует снимок из своего ответа.
     */
    invalidateDraftSnapshot(state): EditorState {
      return { ...state, serverUpdatedAt: null };
    },
    /**
     * WEB-1831: поставить снимок сверки в ИЗВЕСТНЫЙ свежий штамп записи —
     * ход ИИ-чата теперь возвращает свой `updatedAt` (оба пути: прямой ответ
     * POST и добор опросом listTurns, заплатка WEB-1800). Это строго лучше
     * гашения снимка (`invalidateDraftSnapshot`): пока снимок погашен, сверка
     * WEB-1333 не работает и параллельная правка второго участника
     * затирается молча. Гашение остаётся запасным путём — для хода, который
     * оборвался исключением (сервер мог его записать, а штампа мы не знаем),
     * и для старого бэка без поля.
     */
    setDraftSnapshot(state, action: PayloadAction<string>): EditorState {
      return { ...state, serverUpdatedAt: action.payload };
    },
    // Ревизия пачки 3, находка 9: ручной редьюсер `setSourceLanguage` здесь
    // не нужен и удалён. Он никем не диспатчился (мёртвый экспорт) и дублировал
    // половину `changeSourceLanguage.fulfilled` — а половина опасна: язык без
    // пересинхронизации `serverUpdatedAt` даёт ложный 409 STALE_DRAFT
    // следующему сохранению черновика. Правда одна: extraReducer ниже.
    resetEditor(): EditorState {
      return { ...initialState };
    },
    setName(state, action: PayloadAction<string>): EditorState {
      return { ...state, name: action.payload, isDirty: true };
    },
    selectScreen(state, action: PayloadAction<string>): EditorState {
      return {
        ...state,
        selectedScreenId: action.payload,
        selectedBlockId: null,
      };
    },
    selectBlock(state, action: PayloadAction<string | null>): EditorState {
      return { ...state, selectedBlockId: action.payload };
    },
    // WEB-683: transient UI toggle — intentionally NOT marked isDirty (it is
    // not part of the saved content) and NOT wrapped in withHistory (it must
    // not be undo/redo-able, nor snapshotted).
    setCookiePreviewVisible(
      state,
      action: PayloadAction<boolean>,
    ): EditorState {
      return { ...state, cookiePreviewVisible: action.payload };
    },
    setDownsellPreviewVisible(
      state,
      action: PayloadAction<boolean>,
    ): EditorState {
      return { ...state, downsellPreviewVisible: action.payload };
    },
    // WEB-1753: выбранный вид блока «Оффер удержания» на канвасе. Как и два
    // соседа выше — вне withHistory: это взгляд автора на блок, а не правка
    // воронки (иначе «отменить» отменяло бы переключение вида, а сохранение
    // уносило бы его на сервер).
    setDownsellOfferPreviewState(
      state,
      action: PayloadAction<DownsellOfferPreviewState>,
    ): EditorState {
      return { ...state, downsellOfferPreviewState: action.payload };
    },
    addScreen: withHistory((state): EditorState => {
      // WEB-3764: одна фабрика экрана на редактор части и холст воронки
      const screen = newScreen(state.content);
      return {
        ...state,
        content: [...state.content, screen],
        selectedScreenId: screen.id,
        selectedBlockId: null,
        isDirty: true,
      };
    }),
    removeScreen: withHistoryPayload(
      (state, action: PayloadAction<string>): EditorState => {
        const filtered = state.content.filter((s) => s.id !== action.payload);
        const newSelectedId =
          state.selectedScreenId === action.payload
            ? (filtered[0]?.id ?? null)
            : state.selectedScreenId;
        return {
          ...state,
          content: filtered,
          selectedScreenId: newSelectedId,
          selectedBlockId: null,
          isDirty: true,
        };
      },
    ),
    renameScreen: withHistoryPayload(
      (
        state,
        action: PayloadAction<{ screenId: string; name: string }>,
      ): EditorState => {
        return {
          ...state,
          content: state.content.map((s) =>
            s.id === action.payload.screenId
              ? { ...s, name: action.payload.name }
              : s,
          ),
          isDirty: true,
        };
      },
    ),
    /**
     * WEB: mark (or unmark) a screen as the paywall's post-purchase screen.
     * Invariant: at most one screen carries isPostPurchase — enabling it on
     * `screenId` clears the flag on every other screen in the same content
     * array. Disabling only clears the target screen.
     */
    setScreenPostPurchase: withHistoryPayload(
      (
        state,
        action: PayloadAction<{ screenId: string; value: boolean }>,
      ): EditorState => {
        const { screenId, value } = action.payload;
        return {
          ...state,
          content: state.content.map((s) => {
            if (s.id === screenId) {
              return { ...s, isPostPurchase: value };
            }
            return value && s.isPostPurchase
              ? { ...s, isPostPurchase: false }
              : s;
          }),
          isDirty: true,
        };
      },
    ),
    /**
     * WEB-927 атом 4: фоновая картинка экрана. Пустая строка = «картинки нет»,
     * поэтому ключ imageUrl удаляется, а не сохраняется пустым: резолвер
     * (resolveScreenLayout) считает наличие фона именно по непустому URL.
     */
    setScreenBackgroundImageUrl: withHistoryPayload(
      (
        state,
        action: PayloadAction<{ screenId: string; imageUrl: string }>,
      ): EditorState => {
        const { screenId, imageUrl } = action.payload;
        const trimmed = imageUrl.trim();
        return {
          ...state,
          content: state.content.map((s) =>
            s.id === screenId
              ? {
                  ...s,
                  // WEB-1873: и режим, в котором эту картинку кто-то нарисует
                  // (иначе фон уходил в контент и не показывался никому).
                  background: withRenderableImageMode(
                    withBackgroundKey(
                      s.background,
                      'imageUrl',
                      trimmed || undefined,
                    ),
                    state.editorType,
                    trimmed.length > 0,
                  ),
                }
              : s,
          ),
          isDirty: true,
        };
      },
    ),
    setScreenBackgroundMode: withHistoryPayload(
      (
        state,
        action: PayloadAction<{
          screenId: string;
          mode: "card" | "full-screen";
        }>,
      ): EditorState => {
        const { screenId, mode } = action.payload;
        return {
          ...state,
          content: state.content.map((s) =>
            s.id === screenId
              ? { ...s, background: { ...(s.background ?? {}), mode } }
              : s,
          ),
          isDirty: true,
        };
      },
    ),
    /**
     * WEB-927 атом 4: вуаль поверх фона. `overlay: undefined` = «как в стиле» —
     * ключ ФИЗИЧЕСКИ удаляется, иначе экран навсегда перестанет слушать
     * дефолт стиль-пака (resolveScreenLayout смотрит именно на отсутствие поля).
     */
    setScreenBackgroundOverlay: withHistoryPayload(
      (
        state,
        action: PayloadAction<{
          screenId: string;
          overlay?: "scrim-bottom" | "scrim-full" | "none";
        }>,
      ): EditorState => {
        const { screenId, overlay } = action.payload;
        return {
          ...state,
          content: state.content.map((s) =>
            s.id === screenId
              ? {
                  ...s,
                  background: withBackgroundKey(s.background, 'overlay', overlay),
                }
              : s,
          ),
          isDirty: true,
        };
      },
    ),
    /**
     * WEB-1805: собственный цвет фона ЭКРАНА. `color: undefined` = «фон
     * воронки» — ключ УДАЛЯЕТСЯ так же, как у вуали: пустая строка не прошла
     * бы схему экрана, а сам факт наличия ключа означал бы «экран задал цвет».
     * Остальной фон (картинка, режим, вуаль) не трогается: сброс цвета не
     * имеет права сносить соседние настройки.
     */
    setScreenBackgroundColor: withHistoryPayload(
      (
        state,
        action: PayloadAction<{ screenId: string; color?: string }>,
      ): EditorState => {
        const { screenId, color } = action.payload;
        return {
          ...state,
          content: state.content.map((s) =>
            s.id === screenId
              ? { ...s, background: withBackgroundKey(s.background, 'color', color) }
              : s,
          ),
          isDirty: true,
        };
      },
    ),
    /** WEB-1805: собственный градиент фона ЭКРАНА; undefined снимает ключ. */
    setScreenBackgroundGradient: withHistoryPayload(
      (
        state,
        action: PayloadAction<{
          screenId: string;
          gradient?: { from: string; to: string; angle: number };
        }>,
      ): EditorState => {
        const { screenId, gradient } = action.payload;
        return {
          ...state,
          content: state.content.map((s) =>
            s.id === screenId
              ? {
                  ...s,
                  background: withBackgroundKey(s.background, 'gradient', gradient),
                }
              : s,
          ),
          isDirty: true,
        };
      },
    ),
    setScreenAlignY: withHistoryPayload(
      (
        state,
        action: PayloadAction<{
          screenId: string;
          alignY?: "top" | "center" | "bottom";
        }>,
      ): EditorState => {
        const { screenId, alignY } = action.payload;
        return {
          ...state,
          content: state.content.map((s) =>
            s.id === screenId
              ? { ...s, layout: withLayoutKey(s.layout, "alignY", alignY) }
              : s,
          ),
          isDirty: true,
        };
      },
    ),
    setScreenAlignX: withHistoryPayload(
      (
        state,
        action: PayloadAction<{
          screenId: string;
          alignX?: "left" | "center" | "right";
        }>,
      ): EditorState => {
        const { screenId, alignX } = action.payload;
        return {
          ...state,
          content: state.content.map((s) =>
            s.id === screenId
              ? { ...s, layout: withLayoutKey(s.layout, "alignX", alignX) }
              : s,
          ),
          isDirty: true,
        };
      },
    ),
    reorderScreens: withHistoryPayload(
      (state, action: PayloadAction<string[]>): EditorState => {
        const idOrder = action.payload;
        const sorted = idOrder
          .map((id) => state.content.find((s) => s.id === id))
          .filter(Boolean) as EditorScreen[];
        return { ...state, content: sorted, isDirty: true };
      },
    ),
    addBlock: withHistoryPayload(
      (
        state,
        action: PayloadAction<{ screenId: string; blockType: BlockType }>,
      ): EditorState => {
        const { screenId, blockType } = action.payload;
        const makeBlock = (type: BlockType): EditorBlock => ({
          id: generateId(),
          type,
          content: "",
          // WEB-785: дефолты кнопок — на языке воронки, а не английские.
          ...buildNewBlockDefaults(type, state.language),
          ...packBlockSettings(state.settings.stylePackId, type), // WEB-3718
        });
        const newBlock = makeBlock(blockType);
        // A plan_selector only drives checkout when a pay button reads the picked
        // plan (resolvePriceId, PublicPaywall §4.3a). Auto-add the pay button right
        // below it so selection + checkout arrive as one unit — unless the screen
        // already has a pay button (avoid duplicates).
        const targetScreen = state.content.find((s) => s.id === screenId);
        const screenHasPayButton = (targetScreen?.blocks ?? []).some(
          (b) =>
            b.type === "button_to_pay" || b.type === "stripe_checkout_form",
        );
        const blocksToAdd =
          blockType === "plan_selector" && !screenHasPayButton
            ? [newBlock, makeBlock("button_to_pay")]
            : [newBlock];
        return {
          ...state,
          content: state.content.map((s) =>
            s.id === screenId
              ? { ...s, blocks: [...s.blocks, ...blocksToAdd] }
              : s,
          ),
          selectedBlockId: newBlock.id,
          isDirty: true,
        };
      },
    ),
    removeBlock: withHistoryPayload(
      (
        state,
        action: PayloadAction<{ screenId: string; blockId: string }>,
      ): EditorState => {
        const { screenId, blockId } = action.payload;
        const content = state.content.map((s) =>
          s.id === screenId
            ? {
                ...s,
                // WEB-3879: удалён блок выбора — ссылки на его варианты сняты.
                blocks: pruneDanglingOptionRefs(
                  s.blocks.filter((b) => b.id !== blockId),
                ),
              }
            : s,
        );
        return {
          ...state,
          content,
          // WEB-2475: правила на блок сняты в том же шаге истории.
          logic: pruneDanglingRuleRefs(state.content, content, state.logic)
            .logic,
          selectedBlockId:
            state.selectedBlockId === blockId ? null : state.selectedBlockId,
          isDirty: true,
        };
      },
    ),
    reorderBlocks: withHistoryPayload(
      (
        state,
        action: PayloadAction<{ screenId: string; blockIds: string[] }>,
      ): EditorState => {
        const { screenId, blockIds } = action.payload;
        return {
          ...state,
          content: state.content.map((s) => {
            if (s.id !== screenId) return s;
            const sorted = blockIds
              .map((id) => s.blocks.find((b) => b.id === id))
              .filter(Boolean) as EditorBlock[];
            return { ...s, blocks: sorted };
          }),
          isDirty: true,
        };
      },
    ),
    updateBlock: withHistoryPayload(
      (
        state,
        action: PayloadAction<{
          screenId: string;
          blockId: string;
          updates: Partial<EditorBlock>;
        }>,
      ): EditorState => {
        const { screenId, blockId } = action.payload;
        // WEB-1532 (ревизия пачек 7-8, находка 1): вторая дверь для семейств,
        // которые пишут свой список вариантов НЕ через addBlockOption —
        // выпадающий список (`selectOptions`) и картиночный выбор
        // (`imageChoiceOptions`). Их кнопки заглушены, но кнопка — первая
        // дверь; любой другой путь записи перешагивал потолок молча, и тогда
        // сервер отбивал 400-й ВЕСЬ контент воронки.
        // Отклоняется только РОСТ выше потолка: уменьшение разрешено всегда,
        // иначе старую воронку с перебором нельзя было бы починить руками.
        const target = state.content
          .find((s) => s.id === screenId)
          ?.blocks.find((b) => b.id === blockId);
        const updates = withOptionCapEnforced(target, action.payload.updates);
        if (Object.keys(updates).length === 0) {
          // Правка целиком свелась к переборному полю — хода нет. Возвращаем
          // ТО ЖЕ состояние, чтобы пустой ход не попал в историю отмены
          // (applyHistoryIfChanged сравнивает по ссылке).
          return state;
        }
        // WEB-955 (ревью п.5): явный undefined в updates означает «убрать поле»,
        // а не «оставить ключ со значением undefined» — иначе undo/redo и любые
        // сравнения видят {background: undefined} ≠ {} (тот же приём, что у
        // фона экрана: rest-деструктуризация вместо мусорного ключа).
        const applyUpdates = (b: EditorBlock): EditorBlock => {
          const merged: Record<string, unknown> = { ...b, ...updates };
          for (const [key, value] of Object.entries(updates)) {
            if (value === undefined) delete merged[key];
          }
          return merged as unknown as EditorBlock;
        };
        return {
          ...state,
          content: state.content.map((s) =>
            s.id === screenId
              ? {
                  ...s,
                  // WEB-3879: новый список вариантов без варианта — ссылка
                  // на него снята в том же шаге истории.
                  blocks: pruneDanglingOptionRefs(
                    s.blocks.map((b) =>
                      b.id === blockId ? applyUpdates(b) : b,
                    ),
                  ),
                }
              : s,
          ),
          isDirty: true,
        };
      },
    ),
    addBlockOption: withHistoryPayload(
      (
        state,
        action: PayloadAction<{ screenId: string; blockId: string }>,
      ): EditorState => {
        const { screenId, blockId } = action.payload;
        // WEB-1532: сервер принимает не больше MAX_BLOCK_OPTIONS вариантов, и
        // перебор отбивал 400-й ВЕСЬ контент воронки — правки на всех экранах
        // не сохранялись. Возвращаем то же состояние (не копию с isDirty), и
        // тогда applyHistoryIfChanged не кладёт пустой ход в историю отмены.
        const target = state.content
          .find((s) => s.id === screenId)
          ?.blocks.find((b) => b.id === blockId);
        if ((target?.options?.length ?? 0) >= MAX_BLOCK_OPTIONS) {
          return state;
        }
        const newOption: BlockOption = {
          id: generateId(),
          label: "New option",
        };
        return {
          ...state,
          content: state.content.map((s) =>
            s.id === screenId
              ? {
                  ...s,
                  blocks: s.blocks.map((b) =>
                    b.id === blockId
                      ? { ...b, options: [...(b.options ?? []), newOption] }
                      : b,
                  ),
                }
              : s,
          ),
          isDirty: true,
        };
      },
    ),
    removeBlockOption: withHistoryPayload(
      (
        state,
        action: PayloadAction<{
          screenId: string;
          blockId: string;
          optionId: string;
        }>,
      ): EditorState => {
        const { screenId, blockId, optionId } = action.payload;
        const content = state.content.map((s) =>
          s.id === screenId
            ? {
                ...s,
                // WEB-3879: снят вариант — снята и ссылка на него.
                blocks: pruneDanglingOptionRefs(
                  s.blocks.map((b) =>
                    b.id === blockId
                      ? {
                          ...b,
                          options: (b.options ?? []).filter(
                            (o) => o.id !== optionId,
                          ),
                        }
                      : b,
                  ),
                ),
              }
            : s,
        );
        return {
          ...state,
          content,
          // WEB-2475: правила на вариант сняты в том же шаге истории.
          logic: pruneDanglingRuleRefs(state.content, content, state.logic)
            .logic,
          isDirty: true,
        };
      },
    ),
    updateBlockOption: withHistoryPayload(
      (
        state,
        action: PayloadAction<{
          screenId: string;
          blockId: string;
          optionId: string;
          label: string;
          displayLabel?: string;
        }>,
      ): EditorState => {
        const { screenId, blockId, optionId, label, displayLabel } =
          action.payload;
        return {
          ...state,
          content: state.content.map((s) =>
            s.id === screenId
              ? {
                  ...s,
                  blocks: s.blocks.map((b) =>
                    b.id === blockId
                      ? {
                          ...b,
                          options: (b.options ?? []).map((o) =>
                            // WEB-809 wave 2b: label = plain-проекция, displayLabel = rich.
                            o.id === optionId
                              ? {
                                  ...o,
                                  label,
                                  ...(displayLabel !== undefined
                                    ? { displayLabel }
                                    : {}),
                                }
                              : o,
                          ),
                        }
                      : b,
                  ),
                }
              : s,
          ),
          isDirty: true,
        };
      },
    ),
    markSaved(state): EditorState {
      // WEB-608: автосейв гасит isDirty, но НЕ manualEditsSinceTurn — иначе
      // сигнал «после хода были ручные правки» стирался за 1-2 сек и
      // revert-confirm не появлялся.
      return { ...state, isDirty: false };
    },
    /**
     * WEB-608: AI-ход применён к черновику — правки поглощены ходом, сбрасываем
     * «ручные правки после хода». Диспатчится в ThreePanelEditor последним в
     * последовательности применения хода (после replaceContent/setLogic/
     * setBackground), поэтому порядок частных диспатчей значения не имеет.
     */
    markTurnApplied(state): EditorState {
      return { ...state, manualEditsSinceTurn: false };
    },
    // Решение владельца продукта 09.08 (хвост WEB-1003 п.4): undefined —
    // «Без темы» из галереи (снятие ранее выбранной темы, симметрично
    // setStylePackId), не просто «нет аргумента». Тип действия должен явно
    // это допускать, иначе TS не даст диспатчнуть undefined из галереи.
    //
    // Ревизия пачки 11 (зеркало WEB-934 applySetTheme, бэк
    // settings-operations.ts:115-121): палитра/фон/цвет текста перекрывают
    // тему с наивысшим приоритетом (buildFunnelSurfaceStyle кладёт палитру
    // ПОВЕРХ темы) — выбор новой темы из галереи, оставляющий их висеть,
    // визуально ничего не меняет и врёт тостом «Тема применена». Снимаем те
    // же три поля, что и бэковый applySetTheme. Форма (stylePackId), шрифт,
    // логотип/фавикон — другая ось, их не трогаем.
    // WEB-1239 (п.6): редьюсеры оформления обёрнуты историей — «Отменить
    // действие» возвращает тему, палитру, шрифт, логотип и полосу прогресса
    // так же, как возвращает блоки. До карточки оформление не возвращалось
    // ничем, а на живую страницу уезжало сразу.
    setThemeId: withHistoryPayload(
      (state, action: PayloadAction<string | undefined>): EditorState => ({
        ...state,
        settings: withTheme(state.settings, action.payload), // WEB-3766: одна дверь с окном «Шаблоны» карты
        isDirty: true,
      }),
    ),
    /**
     * Стиль-пак — вторая ось оформления рядом с темой/палитрой: задаёт ФОРМУ
     * (скругления, плотность, типографскую шкалу, хром, поведение CTA).
     * Отдельный action, чтобы смена стиля не трогала цвет и наоборот.
     */
    // WEB-1003 п.4: undefined — «Без оформления» из галереи (снятие ранее
    // выбранного пака), не просто «нет аргумента». Тип действия должен явно
    // это допускать, иначе TS не даст диспатчнуть undefined из галереи.
    setStylePackId: withHistoryPayload(
      (state, action: PayloadAction<string | undefined>): EditorState => {
        // WEB-3718; WEB-4151: «Оригинал» и пак у воронки со слоем меняют и блоки — одним шагом истории
        const { settings, screens: content } = withLookChoice(state.settings, state.content, action.payload);
        return settings === state.settings && content === state.content ? state : { ...state, settings, content, isDirty: true };
      },
    ),
    /**
     * WEB-853: режим показа пейволла. Обязательный режим — это ОТСУТСТВИЕ
     * полей, а не значение 'mandatory' в базе: дефолт и явный выбор должны
     * быть одним и тем же состоянием, иначе «сбросил в обязательный» и
     * «никогда не настраивал» разошлись бы по поведению после любой правки
     * дефолта. Задержка живёт только у режима delayed — иначе при
     * переключении режимов в настройках копится мёртвое поле.
     */
    setCloseMode: withHistoryPayload(
      (
        state,
        action: PayloadAction<{
          mode: PaywallCloseMode;
          delaySeconds?: number;
        }>,
      ): EditorState => ({
        ...state,
        settings: withCloseMode(state.settings, action.payload),
        isDirty: true,
      }),
    ),
    /** WEB-922: логотип воронки (пустая строка снимает поле). */
    setLogoUrl: withHistoryPayload(
      (state, action: PayloadAction<string>): EditorState => ({
        ...state,
        settings: withLogoUrl(state.settings, action.payload),
        isDirty: true,
      }),
    ),
    /** WEB-928: иконка вкладки ресурса (пустая строка снимает поле). */
    setFaviconUrl: withHistoryPayload(
      (state, action: PayloadAction<string>): EditorState => ({
        ...state,
        settings: withFaviconUrl(state.settings, action.payload),
        isDirty: true,
      }),
    ),
    /**
     * WEB-928: «иконка квиза действует на всю воронку». Снятие флага удаляет
     * поле — дефолт «выключено» в настройках не хранится.
     */
    setFaviconApplyToFunnel: withHistoryPayload(
      (state, action: PayloadAction<boolean>): EditorState => ({
        ...state,
        settings: withFaviconApplyToFunnel(state.settings, action.payload),
        isDirty: true,
      }),
    ),
    replaceContent: withHistoryPayload(
      (state, action: PayloadAction<EditorScreen[]>): EditorState =>
        replaceContentState(state, action.payload),
    ),
    setBackground: withHistoryPayload(
      (
        state,
        action: PayloadAction<FunnelBackground | undefined>,
      ): EditorState => {
        return {
          ...state,
          settings: withColorsTouched({ ...state.settings, background: action.payload }),
          isDirty: true,
        };
      },
    ),
    /**
     * txt-1: Set or clear the funnel-level text color immutably.
     * Pass a CSS color string to set, or undefined to clear (reset to theme default).
     */
    setTextColor: withHistoryPayload(
      (state, action: PayloadAction<string | undefined>): EditorState => {
        return {
          ...state,
          settings: withColorsTouched({ ...state.settings, textColor: action.payload }),
          isDirty: true,
        };
      },
    ),
    /**
     * WEB-934 refix: настройки, вернувшиеся из хода AI-чата. ЗАМЕЩАЮТ текущие
     * целиком — ход уже сохранён на сервере, и стор обязан совпадать с базой.
     *
     * Почему не merge: стилевой ход не только пишет поля, но и СНИМАЕТ
     * перекрывающие слои (на бэке applySetTheme убирает palette/background/
     * textColor, applySetPalette — background/textColor). При слиянии снятая
     * палитра осталась бы в сторе, снова победила бы тему в сборке поверхности
     * (buildFunnelSurfaceStyle: палитра кладётся ПОВЕРХ темы) и ближайший
     * автосейв вернул бы её в базу — «правка есть, а на экране нет» (шаги 1-2
     * приёмки WEB-934).
     *
     * isDirty намеренно НЕ взводится: это не правка пользователя, а синхронизация
     * с уже сохранённым сервером состоянием.
     */
    applyTurnSettings(
      state,
      action: PayloadAction<EditorSettings>,
    ): EditorState {
      return { ...state, settings: { ...action.payload } };
    },
    /**
     * ux-2: Append one AI chat entry immutably.
     * The entry must use a numeric timestamp (not a Date) to keep it serializable.
     */
    /**
     * WEB-2382 п.3 — ИТОГ ХОДА ИИ-ЧАТА ОДНИМ ДЕЙСТВИЕМ.
     *
     * Раньше редактор клал итог тремя диспатчами подряд: `replaceContent`
     * (обёрнут историей), `setLogic` (обёрнут историей) и `applyTurnSettings`
     * (историю не пишет вовсе). «Отменить действие» после хода снимало ровно
     * последний шаг — то есть логику, оставляя экраны хода, а оформление хода
     * не возвращалось никогда. Автор получал полуоткаченную воронку и
     * сохранял именно её.
     *
     * Здесь всё три части кладутся вместе и обёрнуты `withHistoryPayload`:
     * снимок истории у среза и так хранит `content + logic + settings`
     * (editor/history.ts), поэтому одна отмена возвращает ход целиком.
     *
     * Оформление ЗАМЕЩАЕТСЯ целиком, а не сливается, — ровно по тем же
     * причинам, что у `applyTurnSettings` (см. его докблок: стилевой ход
     * снимает перекрывающие слои, и слияние вернуло бы снятую палитру).
     */
    applyTurnResult: withHistoryPayload(
      (
        state,
        action: PayloadAction<{
          content: EditorScreen[];
          logic?: Rule[];
          settings?: EditorSettings;
          /** WEB-2462: язык от хода (`set_funnel_language`); сервер уже записал, в историю не входит. */
          language?: string;
        }>,
      ): EditorState => {
        const withContent = replaceContentState(state, action.payload.content);
        return {
          ...withContent,
          ...(action.payload.language !== undefined
            ? { language: action.payload.language }
            : {}),
          logic:
            action.payload.logic !== undefined
              ? [...action.payload.logic]
              : withContent.logic,
          settings:
            action.payload.settings !== undefined
              ? { ...action.payload.settings }
              : withContent.settings,
        };
      },
    ),
    addAiChatEntry(state, action: PayloadAction<AiChatEntry>): EditorState {
      return {
        ...state,
        aiChatHistory: [...state.aiChatHistory, action.payload],
      };
    },
    /**
     * WEB-115 Slice A: Replace the entire logic array immutably.
     * Validation is the caller's responsibility (run logicSchema.safeParse
     * before dispatching in production; UI shows inline errors).
     */
    setLogic: withHistoryPayload(
      (state, action: PayloadAction<Rule[]>): EditorState => {
        return { ...state, logic: [...action.payload], isDirty: true };
      },
    ),
    // WEB-347: setAppDestination removed — appUrl/storeUrl no longer in EditorState.
    // App/store destination is managed via project.bridgeConfig (App Connection section).
    /** WEB-624: отмена и повтор — шаги живут в `./editor/history`. */
    undo(state): EditorState {
      return undoState(state);
    },
    redo(state): EditorState {
      return redoState(state);
    },
  },
  extraReducers: (builder) => {
    addScreenFieldCases(builder)
      // Действия `./editor/actions.ts` (описания там); addCase — до addMatcher.
      .addCase(setTierLimitReason, (state, action): EditorState => {
        return { ...state, tierLimitReasonCode: action.payload };
      })
      .addCase(duplicateScreen, withHistoryPayload(duplicateScreenState))
      .addCase(duplicateBlock, withHistoryPayload(duplicateBlockState))
      .addCase(loadQuizForEditor.pending, (state, action) => ({
        ...state,
        isLoading: true, loadRequestId: action.meta?.requestId, // з6: см. isCurrentLoad
        // WEB-3366: сеанс открытия ставит УСПЕШНАЯ загрузка (fulfilled), не её
        // начало: упавшая загрузка другого ресурса не отрезает ответ записи открытого.
        error: null,
      }))
      .addCase(loadQuizForEditor.fulfilled, (state, action) => {
        if (!isCurrentLoad(state, action.meta?.requestId)) return state; // з6: опоздавший ответ открытия
        const { projectId, data } = action.payload;
        // WEB-2561 / WEB-3366: сеанс открытия — по успешной загрузке; перечитывание того же ресурса — прежний.
        const openSession = nextOpenSession(state, "quiz", data.id as string, action.meta?.requestId);
        // WEB-384 data-loss: manual block edits autosave into draftContent; load
        // must prefer it over the stale published content (mirrors draftLogic).
        const content = parseContent(data.draftContent ?? data.content);
        return {
          ...state,
          isLoading: false, openSession,
          editorType: "quiz",
          resourceId: data.id as string,
          resourceSlug: (data.slug as string) ?? null,
          projectId,
          // WEB-3001: ссылка на породившую генерацию — на каждой загрузке.
          generationId: generationIdOf(data),
          ownAssetUrlPrefixes: ownAssetUrlPrefixesOf(data.ownAssetUrlPrefixes), // WEB-3872
          name: data.name as string,
          content,
          // WEB-1239: редактор показывает ЧЕРНОВОЕ оформление — тем же
          // правилом, каким берётся черновое содержимое строкой выше (шрам
          // WEB-384: половина пары draft/published без симметричного чтения =
          // тихая потеря правок). Черновика нет — сервер отдаёт в этом поле
          // опубликованное, фолбэк на `settings` остаётся для ответов старого
          // бэка, выкаченного раньше фронта.
          settings:
            ((data.draftSettings ?? data.settings) as EditorSettings) ?? {},
          selectedScreenId: content[0]?.id ?? null,
          selectedBlockId: null,
          isDirty: false, isSaving: isOpenResource(state, "quiz", data.id as string) ? state.isSaving : false,
          // WEB-608: свежая загрузка с сервера (в т.ч. после revert) = ручных
          // правок ещё нет.
          manualEditsSinceTurn: false,
          error: null,
          aiChatHistory: [],
          logic: loadedLogicOf(data), // WEB-384: черновые правила первыми
          // WEB-430: подхватываем язык квиза (напр. язык AI-генерации).
          // Ревизия WEB-1907: фолбэк практически недостижим — колонка языка
          // не nullable, сервер присылает её всегда; он страхует лишь ответ
          // без поля (старый кэш, урезанная выборка). WEB-1909: значение — та
          // же константа, что ставит сервер при создании.
          language: (data.language as string) || DEFAULT_FUNNEL_LANGUAGE,
          // WEB-1333 (п.5): свежая загрузка = свежий снимок updatedAt.
          serverUpdatedAt: (data.updatedAt as string | undefined) ?? null,
          // WEB-1505: признак блокировки по понижению тарифа. Пишется на
          // КАЖДОЙ загрузке (а не только при true) — иначе после перехода от
          // заблокированного ресурса к разрешённому в состоянии осталось бы
          // старое true и конструктор гасил бы правку зря.
          isReadonly: data.isReadonly === true,
          // WEB-2230: ни разу не публиковался — перевод пойдёт с черновика.
          neverPublished: neverPublishedOf(data),
          // WEB-347: appUrl/storeUrl removed from EditorState.
          // WEB-624: loading a quiz is not undoable — clear any stale history.
          past: [],
          future: [],
        };
      })
      .addCase(loadQuizForEditor.rejected, (state, action) => !isCurrentLoad(state, action.meta?.requestId) ? state : ({
        ...state,
        isLoading: false,
        error: rejectedApiError(action),
      }))
      .addCase(loadPaywallForEditor.pending, (state, action) => ({
        ...state,
        isLoading: true, loadRequestId: action.meta?.requestId, // з6: см. isCurrentLoad
        // WEB-3366: сеанс открытия ставит УСПЕШНАЯ загрузка (fulfilled), не её
        // начало: упавшая загрузка другого ресурса не отрезает ответ записи открытого.
        error: null,
      }))
      .addCase(loadPaywallForEditor.fulfilled, (state, action) => {
        if (!isCurrentLoad(state, action.meta?.requestId)) return state; // з6: опоздавший ответ открытия
        const { projectId, data } = action.payload;
        // WEB-2561 / WEB-3366: сеанс открытия — по успешной загрузке; перечитывание того же ресурса — прежний.
        const openSession = nextOpenSession(state, "paywall", data.id as string, action.meta?.requestId);
        // WEB-384 data-loss: prefer draftContent so manual paywall edits survive reload.
        const content = parseContent(data.draftContent ?? data.content);
        return {
          ...state,
          isLoading: false, openSession,
          editorType: "paywall",
          resourceId: data.id as string,
          resourceSlug: (data.slug as string) ?? null,
          projectId,
          // WEB-3001: ссылка на породившую генерацию — на каждой загрузке.
          generationId: generationIdOf(data),
          ownAssetUrlPrefixes: ownAssetUrlPrefixesOf(data.ownAssetUrlPrefixes), // WEB-3872
          name: data.name as string,
          content,
          // WEB-1239: редактор показывает ЧЕРНОВОЕ оформление — тем же
          // правилом, каким берётся черновое содержимое строкой выше (шрам
          // WEB-384: половина пары draft/published без симметричного чтения =
          // тихая потеря правок). Черновика нет — сервер отдаёт в этом поле
          // опубликованное, фолбэк на `settings` остаётся для ответов старого
          // бэка, выкаченного раньше фронта.
          settings:
            ((data.draftSettings ?? data.settings) as EditorSettings) ?? {},
          selectedScreenId: content[0]?.id ?? null,
          selectedBlockId: null,
          isDirty: false, isSaving: isOpenResource(state, "paywall", data.id as string) ? state.isSaving : false,
          // WEB-608: свежая загрузка (в т.ч. после revert) = ручных правок нет.
          manualEditsSinceTurn: false,
          error: null,
          aiChatHistory: [],
          logic: loadedLogicOf(data), // WEB-384: черновые правила первыми
          // WEB-430: подхватываем язык пейволла. Ревизия WEB-1907: фолбэк —
          // та же страховка «ответ без поля», что у загрузки квиза выше, и то
          // же значение, что ставит сервер при создании (WEB-1909, константа).
          language: (data.language as string) || DEFAULT_FUNNEL_LANGUAGE,
          // WEB-1333 (п.5): свежая загрузка = свежий снимок updatedAt.
          serverUpdatedAt: (data.updatedAt as string | undefined) ?? null,
          // WEB-1505: см. загрузку квиза выше — признак пишется на каждой
          // загрузке, чтобы прошлая блокировка не «залипала».
          isReadonly: data.isReadonly === true,
          // WEB-2230: ни разу не публиковался — перевод пойдёт с черновика.
          neverPublished: neverPublishedOf(data),
          // WEB-347: appUrl/storeUrl removed — source = project.bridgeConfig.storeUrl.
          // WEB-624: loading a paywall is not undoable — clear any stale history.
          past: [],
          future: [],
        };
      })
      .addCase(loadPaywallForEditor.rejected, (state, action) => !isCurrentLoad(state, action.meta?.requestId) ? state : ({
        ...state,
        isLoading: false,
        error: rejectedApiError(action),
      }))
      // WEB-2586: начало, успех и отказ записи принимаются только для открытого
      // ресурса (whenOpen / acceptWriteResponse): сохранение при уходе, ответ
      // соседнего квиза и его отказ состояние открытого не трогают.
      // WEB-2561: успех и отказ сохранения — ещё и только своего сеанса
      // открытия (meta санка): ответ записи, ушедшей до ухода из редактора,
      // не ложится в тот же квиз, открытый заново.
      .addCase(saveQuizDraft.pending, (state, action) =>
        whenOpen(state, "quiz", action.meta.arg.quizId, () => ({ ...state, isSaving: true })),
      )
      // WEB-1333 (п.5): снимок serverUpdatedAt едет на updatedAt ответа.
      // WEB-2552: «не сохранено» гаснет, только если после отправки правок
      // не было (правка, пока запрос в пути, иначе пропадала молча).
      .addCase(saveQuizDraft.fulfilled, (state, action) =>
        acceptWriteResponse(state, "quiz", action.meta.arg.quizId, action.payload,
          storeHoldsSentDraft(state, action.meta.arg, action.meta), action.meta),
      )
      .addCase(saveQuizDraft.rejected, (state, action) =>
        whenOpen(state, "quiz", action.meta.arg.quizId, () => rejectedBuildState(state, action.payload), action.meta),
      )
      .addCase(savePaywallDraft.pending, (state, action) =>
        whenOpen(state, "paywall", action.meta.arg.paywallId, () => ({ ...state, isSaving: true })),
      )
      .addCase(savePaywallDraft.fulfilled, (state, action) =>
        acceptWriteResponse(state, "paywall", action.meta.arg.paywallId, action.payload,
          storeHoldsSentDraft(state, action.meta.arg, action.meta), action.meta),
      )
      .addCase(savePaywallDraft.rejected, (state, action) =>
        whenOpen(state, "paywall", action.meta.arg.paywallId, () => rejectedBuildState(state, action.payload), action.meta),
      )
      .addCase(publishQuiz.pending, (state, action) =>
        whenOpen(state, "quiz", action.meta.arg.quizId, () => ({ ...state, isSaving: true })),
      )
      // WEB-1333 (хвост): публикация пишет запись повторно со своим updatedAt —
      // без него следующее автосохранение даёт ложный 409.
      .addCase(publishQuiz.fulfilled, (state, action) =>
        acceptPublishResponse(state, "quiz", action.meta.arg.quizId, action.payload),
      )
      .addCase(publishQuiz.rejected, (state, action) =>
        whenOpen(state, "quiz", action.meta.arg.quizId, () => rejectedPublishState(state, action.payload)),
      )
      .addCase(publishPaywall.pending, (state, action) =>
        whenOpen(state, "paywall", action.meta.arg.paywallId, () => ({ ...state, isSaving: true })),
      )
      .addCase(publishPaywall.fulfilled, (state, action) =>
        acceptPublishResponse(state, "paywall", action.meta.arg.paywallId, action.payload),
      )
      .addCase(publishPaywall.rejected, (state, action) =>
        whenOpen(state, "paywall", action.meta.arg.paywallId, () => rejectedPublishState(state, action.payload)),
      )
      // WEB-1833: язык записан на сервере — забираем и его, и свежий штамп
      // записи (см. комментарий у самого thunk'а). Отказ состояние не трогает
      // вовсе: показывать язык, которого на сервере нет, нельзя.
      // WEB-2586: язык и штамп чужого ресурса в открытый не попадают.
      .addCase(changeSourceLanguage.fulfilled, (state, action) =>
        isOpenResource(state, action.meta.arg.resourceType, action.meta.arg.resourceId)
          ? { ...state, language: action.payload.language, serverUpdatedAt: action.payload.updatedAt }
          : state,
      )
      // WEB-608: любой РУЧНОЙ edit-редьюсер этого слайса выставляет isDirty=true
      // → помечаем «ручные правки после хода». Работает поверх case-редьюсеров,
      // поэтому не нужно трогать все 18 мутаторов по отдельности.
      // load/save fulfilled оставляют isDirty=false → сюда не попадают; сброс
      // после хода делает markTurnApplied, после revert — load.fulfilled.
      // WEB-2382 п.6: служебные действия среза исключены перечнем
      // EDITOR_SERVICE_ACTION_NAMES (прежде исключений было ровно два, и
      // снимок сверки / запись в ленту чата / выбор экрана считались ручной
      // правкой — предупреждение при отмене хода показывалось всегда).
      .addMatcher(
        (action): action is { type: string } =>
          typeof (action as { type?: unknown })?.type === "string" &&
          (action as { type: string }).type.startsWith("editor/") &&
          !EDITOR_SERVICE_ACTION_TYPES.has((action as { type: string }).type),
        (state) => {
          if (state.isDirty) state.manualEditsSinceTurn = true;
        },
      );
  },
});

export const {
  resetEditor,
  invalidateDraftSnapshot,
  setDraftSnapshot,
  setName,
  selectScreen,
  selectBlock,
  setCookiePreviewVisible,
  setDownsellPreviewVisible,
  setDownsellOfferPreviewState,
  addScreen,
  removeScreen,
  renameScreen,
  setScreenPostPurchase,
  setScreenBackgroundImageUrl,
  setScreenBackgroundMode,
  setScreenBackgroundOverlay,
  setScreenBackgroundColor,
  setScreenBackgroundGradient,
  setScreenAlignY,
  setScreenAlignX,
  reorderScreens,
  addBlock,
  removeBlock,
  reorderBlocks,
  updateBlock,
  addBlockOption,
  removeBlockOption,
  updateBlockOption,
  markSaved,
  markTurnApplied,
  setThemeId,
  setStylePackId,
  setCloseMode,
  setLogoUrl,
  setFaviconUrl,
  setFaviconApplyToFunnel,
  replaceContent,
  setBackground,
  setTextColor,
  applyTurnSettings,
  applyTurnResult,
  addAiChatEntry,
  setLogic,
  undo,
  redo,
  // WEB-347: setAppDestination removed — appUrl/storeUrl no longer in EditorState.
} = editorSlice.actions;

export default editorSlice.reducer;
